/**
 * Rótulos de período para gráficos e tabelas.
 *
 * Os dois caminhos de entrada informam os meses reais:
 *  - questionário guiado: derivados do mês de referência escolhido pelo usuário
 *  - importação: lidos do cabeçalho da aba INPUT da planilha
 *
 * Quando nenhum dos dois traz data — planilha antiga, cabeçalho sem período —
 * cai para "Mês 1..N". Rótulo genérico é honesto; inventar um mês não é.
 *
 * A quantidade de períodos vem do DADO, não de uma constante: a janela de
 * exibição é a que o payload trouxer, de um mês a N meses.
 */

import { quantidadeDePeriodos } from './series';

const NOMES = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
];

const QUANTIDADE_PADRAO = 3;

/** Quantos rótulos gerar: o pedido explícito, senão o tamanho da série. */
const quantidadeDe = (dre, pedida) => pedida || quantidadeDePeriodos(dre) || QUANTIDADE_PADRAO;

const formatar = (mes, ano) => ({
  mes,
  ano,
  curto: `${NOMES[mes - 1].slice(0, 3)}/${String(ano).slice(2)}`,
  longo: `${NOMES[mes - 1][0].toUpperCase()}${NOMES[mes - 1].slice(1)} de ${ano}`
});

/** Deriva os meses anteriores a partir do mês de referência (o mais recente). */
const derivarDeReferencia = (mesRef, anoRef, quantidade) => {
  const mes = Number(mesRef);
  const ano = Number(anoRef);
  if (!mes || !ano) return null;

  return Array.from({ length: quantidade }, (_, i) => {
    const deslocamento = quantidade - 1 - i;
    const d = new Date(ano, mes - 1 - deslocamento, 1);
    return formatar(d.getMonth() + 1, d.getFullYear());
  });
};

/**
 * Devolve os períodos do DRE já formatados.
 * @returns {Array<{mes, ano, curto, longo}>|null} null quando não há data conhecida
 */
export const periodosDoDRE = (dre, quantidade = null) => {
  if (!dre) return null;
  const n = quantidadeDe(dre, quantidade);

  if (Array.isArray(dre.periodos) && dre.periodos.length) {
    const validos = dre.periodos.filter((p) => p && p.mes && p.ano);
    if (validos.length >= n) {
      return validos.slice(0, n).map((p) => formatar(Number(p.mes), Number(p.ano)));
    }
  }

  if (dre.periodo?.mes && dre.periodo?.ano) {
    return derivarDeReferencia(dre.periodo.mes, dre.periodo.ano, n);
  }

  return null;
};

/**
 * Rótulos prontos para eixo de gráfico e cabeçalho de tabela.
 * @param {'curto'|'longo'} formato
 */
export const rotulosDoDRE = (dre, formato = 'curto', quantidade = null) => {
  const n = quantidadeDe(dre, quantidade);
  const periodos = periodosDoDRE(dre, n);
  if (!periodos) {
    return Array.from({ length: n }, (_, i) => `Mês ${i + 1}`);
  }
  return periodos.map((p) => p[formato]);
};

/** Rótulo de um mês específico (0 = mais antigo). Usado em títulos de seção. */
export const rotuloDoMes = (dre, indice, formato = 'curto') => {
  const rotulos = rotulosDoDRE(dre, formato);
  return rotulos[indice] || `Mês ${indice + 1}`;
};

/** Rótulo do mês mais recente da janela. */
export const rotuloDoUltimoMes = (dre, formato = 'curto') => {
  const rotulos = rotulosDoDRE(dre, formato);
  return rotulos[rotulos.length - 1] || `Mês ${rotulos.length}`;
};
